/**
 * Swoop game view.
 *
 * Layout:
 *   - Centre: play pile (showing the top run + count), with a Pick Up
 *     button next to it.
 *   - Seats around the table: each shows the player's 4 face-up table
 *     stacks (face-up cards visible to all, with a mystery-card-back
 *     hint underneath when one's still face-down).
 *   - Bottom: the local player's hand (scrollable fan).
 *   - Each face-up card in YOUR own row is clickable to add it to the
 *     current play selection.
 *   - The action bar shows "Play N cards", "Pick up pile", and (when
 *     hand+face-ups are gone) "Flip mystery 1..4".
 *
 * Same-rank-only selection: clicking a card of a different rank from
 * what's already selected swaps the selection. Wild swoop cards (10s
 * and jokers) form their own selection group.
 */

import { useMemo, useState } from "react";
import {
  lerpCardWidth,
  useDynamicTableTop,
  useResetOnChange,
  useWindowWidth,
} from "../../lib/uiHooks.js";
import type {
  SwoopPublicState,
} from "../../../../shared/games/swoop.js";
import type { CardId } from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard } from "../../components/PlayingCard.js";
import { SwoopScoreBar } from "./SwoopScoreBar.js";
import { SwoopFlash } from "./SwoopFlash.js";

interface SwoopGameProps {
  state: SwoopPublicState;
  yourHand: CardId[];
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

// ---------------------------------------------------------------------------
// Card / rank helpers (mirror server logic without importing the engine).
// ---------------------------------------------------------------------------

function rankOf(id: CardId): string {
  if (id === "SJ" || id === "BJ") return "*";
  return id[0]!;
}
function isWildId(id: CardId): boolean {
  return id === "SJ" || id === "BJ" || id[0] === "T";
}
function rankValue(r: string): number {
  switch (r) {
    case "A": return 1;
    case "2": return 2;
    case "3": return 3;
    case "4": return 4;
    case "5": return 5;
    case "6": return 6;
    case "7": return 7;
    case "8": return 8;
    case "9": return 9;
    case "J": return 11;
    case "Q": return 12;
    case "K": return 13;
    default: return 0;
  }
}

/**
 * Selection key — same key means "compatible to play together".
 *   - Wild ids share the key "*W" (10s and jokers all stack together).
 *   - Otherwise the rank letter.
 */
function selectionKey(id: CardId): string {
  if (isWildId(id)) return "*W";
  return rankOf(id);
}

// Internal compact discriminator for selected items.
type Selection =
  | { kind: "hand"; handIndex: number }
  | { kind: "table"; tableIndex: number };

export function SwoopGame({
  state,
  yourHand,
  yourSeat,
  events,
  send,
  mobile = false,
}: SwoopGameProps) {
  const [selected, setSelected] = useState<Selection[]>([]);

  const phaseKey = `${state.phase}:${state.roundNumber}:${state.currentSeat}:${state.swoopedPileCount}:${state.playPile.length}`;
  useResetOnChange(phaseKey, () => setSelected([]));

  const windowWidth = useWindowWidth();
  // Phones run rotated to landscape (see `body.mobile-device.in-game-view`
   // CSS), which gives us ~2× the horizontal pixels of the device's
   // portrait width. Mobile baseline is sized for that landscape.
  const pileCardWidth = mobile ? 78 : lerpCardWidth(windowWidth, 80, 120);

  const isMyTurn = yourSeat !== null && state.currentSeat === yourSeat;

  // Compute current selection rank-key (or null if empty).
  const myStack = yourSeat !== null ? state.players[yourSeat]?.table ?? [] : [];
  const selectedCardIds: CardId[] = useMemo(() => {
    const out: CardId[] = [];
    for (const s of selected) {
      if (s.kind === "hand") {
        const c = yourHand[s.handIndex];
        if (c) out.push(c);
      } else {
        const c = myStack[s.tableIndex]?.faceUp;
        if (c) out.push(c);
      }
    }
    return out;
  }, [selected, yourHand, myStack]);

  const selectionKeyOf = (id: CardId) => selectionKey(id);
  const currentSelKey = selectedCardIds.length > 0 ? selectionKeyOf(selectedCardIds[0]!) : null;

  /** Toggle a selection — clicking a card of a different rank clears the
   *  selection and starts a new one with this card. */
  function toggleSelection(item: Selection, id: CardId) {
    setSelected((prev) => {
      const isHand = item.kind === "hand";
      const itemKey = isHand
        ? `hand:${item.handIndex}`
        : `table:${item.tableIndex}`;
      const existsAt = prev.findIndex((s) => {
        if (s.kind !== item.kind) return false;
        if (s.kind === "hand" && item.kind === "hand") return s.handIndex === item.handIndex;
        if (s.kind === "table" && item.kind === "table") return s.tableIndex === item.tableIndex;
        return false;
      });
      // Clicked an existing selection → remove.
      if (existsAt >= 0) {
        const next = [...prev];
        next.splice(existsAt, 1);
        return next;
      }
      // New selection — if it's a different rank, restart.
      const itemSelKey = selectionKeyOf(id);
      const allSameKey = prev.length === 0 || prev.every((s) => {
        const sid =
          s.kind === "hand"
            ? yourHand[s.handIndex]
            : myStack[s.tableIndex]?.faceUp;
        return sid && selectionKeyOf(sid) === itemSelKey;
      });
      if (!allSameKey) {
        return [item];
      }
      // Wilds (10s + jokers) always play as a single card — there's no
      // benefit to stacking multiple wild plays into one turn, since each
      // one already swoops the pile and grants another turn.
      const maxForKind = itemSelKey === "*W" ? 1 : 4;
      if (prev.length >= maxForKind) return prev;
      return [...prev, item];
      void itemKey;
    });
  }

  // Action submitters
  function submitPlay() {
    if (selected.length === 0) return;
    const handCards: CardId[] = [];
    const tableIndices: number[] = [];
    for (const s of selected) {
      if (s.kind === "hand") {
        const c = yourHand[s.handIndex];
        if (c) handCards.push(c);
      } else {
        tableIndices.push(s.tableIndex);
      }
    }
    send({
      t: "action",
      action: { kind: "swoop", payload: { type: "play", handCards, tableIndices } },
    });
    setSelected([]);
  }

  function submitMystery(stackIndex: number) {
    send({
      t: "action",
      action: { kind: "swoop", payload: { type: "play_mystery", stackIndex } },
    });
    setSelected([]);
  }

  function submitPickup() {
    send({ t: "action", action: { kind: "swoop", payload: { type: "pickup" } } });
    setSelected([]);
  }

  // For the legal-hint set: only same-rank cards as current selection (or all if nothing selected).
  const legalSelectionFilter = useMemo<Set<CardId> | undefined>(() => {
    if (currentSelKey === null) return undefined; // anything is selectable
    const allowed = new Set<CardId>();
    for (const c of yourHand) {
      if (selectionKeyOf(c) === currentSelKey) allowed.add(c);
    }
    for (const stack of myStack) {
      if (stack.faceUp && selectionKeyOf(stack.faceUp) === currentSelKey) {
        allowed.add(stack.faceUp);
      }
    }
    return allowed;
  }, [currentSelKey, yourHand, myStack]);

  const tableTopRef = useDynamicTableTop();

  return (
    <div className="game-view swoop-view" ref={tableTopRef}>
      <SwoopScoreBar state={state} yourSeat={yourSeat} />
      <SwoopFlash events={events} state={state} />

      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <SwoopSeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            cardWidth={pileCardWidth}
            mySelections={selected}
            onToggleMyFaceUp={(tableIndex, id) =>
              toggleSelection({ kind: "table", tableIndex }, id)
            }
            onFlipMystery={(stackIndex) => submitMystery(stackIndex)}
            isMyTurn={isMyTurn}
          />
        )}
      >
        <SwoopCenter state={state} cardWidth={pileCardWidth} />
      </CircleTable>

      {isMyTurn && state.phase === "play" && (
        <SwoopActionBar
          selectedCount={selected.length}
          canPlay={selected.length > 0}
          pileCount={state.playPile.length}
          handEmpty={yourHand.length === 0}
          onPlay={submitPlay}
          onPickup={submitPickup}
        />
      )}

      {yourSeat !== null && state.phase === "round_end" && (
        <div className="action-bar">
          <button
            className="primary"
            onClick={() =>
              send({
                t: "action",
                action: { kind: "swoop", payload: { type: "ready_next_round" } },
              })
            }
          >
            Ready for next round
          </button>
        </div>
      )}

      {yourSeat !== null && (
        <Hand
          cards={yourHand}
          selected={new Set(
            selected.filter((s) => s.kind === "hand").map((s) => (s as { handIndex: number }).handIndex),
          )}
          legal={legalSelectionFilter}
          onToggle={(handIndex) =>
            toggleSelection({ kind: "hand", handIndex }, yourHand[handIndex]!)
          }
          sort="swoop"
          mobile={mobile}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat view: nameplate + 4 stacks (face-up over mystery-back).
// ---------------------------------------------------------------------------

function SwoopSeatView({
  seat,
  state,
  yourSeat,
  cardWidth,
  mySelections,
  onToggleMyFaceUp,
  onFlipMystery,
  isMyTurn,
}: {
  seat: number;
  state: SwoopPublicState;
  yourSeat: number | null;
  cardWidth: number;
  mySelections: Selection[];
  onToggleMyFaceUp: (tableIndex: number, id: CardId) => void;
  onFlipMystery: (stackIndex: number) => void;
  isMyTurn: boolean;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const isTurn = state.currentSeat === seat;
  const w = Math.round(cardWidth * 0.62);

  // Which face-ups are currently selected (only relevant for own row).
  const selectedTableIdx = new Set<number>(
    mySelections.filter((s) => s.kind === "table").map((s) => (s as { tableIndex: number }).tableIndex),
  );

  // Mystery-flip enable: only YOUR seat, on YOUR turn, this stack's face-up
  // is gone, and a mystery remains. (Hand doesn't need to be empty — per
  // the rules, you just can't flip a mystery until *its* face-up has been
  // played; other hand/face-up cards can still be in play.)
  const canFlipMystery = (idx: number) => {
    if (!isMe) return false;
    if (!isTurn) return false;
    const stack = p.table[idx];
    if (!stack) return false;
    if (stack.faceUp) return false;
    if (!stack.hasMystery) return false;
    return true;
  };

  return (
    <>
      <div
        className={[
          "nameplate",
          isMe ? "mine" : "opponent",
          isTurn ? "turn" : "",
          p.isOut ? "out" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
          {p.isOut && <span className="role-tag">OUT</span>}
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">hand {p.handCount}</span>
          <span className="rank-line">total {p.totalScore}</span>
        </div>
      </div>
      <div className="swoop-table-row">
        {p.table.map((stack, idx) => {
          const selected = isMe && selectedTableIdx.has(idx);
          const mysteryEnabled = canFlipMystery(idx);
          return (
            <div className="swoop-stack" key={idx} title={`Stack ${idx + 1}`}>
              {stack.faceUp ? (
                <PlayingCard
                  card={stack.faceUp}
                  width={w}
                  selected={selected}
                  selectable={isMe && isMyTurn}
                  dimmed={false}
                  onClick={() => {
                    if (isMe && isMyTurn) onToggleMyFaceUp(idx, stack.faceUp!);
                  }}
                />
              ) : stack.hasMystery ? (
                <button
                  className={`swoop-mystery-btn ${mysteryEnabled ? "active" : ""}`}
                  disabled={!mysteryEnabled}
                  onClick={() => mysteryEnabled && onFlipMystery(idx)}
                  title={mysteryEnabled ? "Flip and play this mystery card" : "Mystery card"}
                >
                  <PlayingCard card={null} width={w} faceDown />
                </button>
              ) : (
                <div className="swoop-stack-empty" style={{ width: w, height: Math.round(w * 1.4) }} />
              )}
              {stack.faceUp && stack.hasMystery && (
                <div className="swoop-mystery-hint" title="Mystery card underneath">
                  +1
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Centre: the play pile (top run prominently displayed)
// ---------------------------------------------------------------------------

function SwoopCenter({
  state,
  cardWidth,
}: {
  state: SwoopPublicState;
  cardWidth: number;
}) {
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Match complete</span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    const last = state.history[state.history.length - 1];
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14, maxWidth: 420 }}>
        <span className="label">
          Round won by {last ? state.players[last.winnerSeat]?.displayName ?? "?" : "?"}
        </span>
        {last && (
          <span className="points" style={{ fontSize: 13, textAlign: "center" }}>
            {state.players.map((p, i) =>
              `${p.displayName} +${last.delta[i]}`
            ).join(" · ")}
          </span>
        )}
      </div>
    );
  }
  // Play phase: show top of pile (up to 4) with run indicator.
  const topCards = state.playPile.slice(-Math.min(4, state.playPile.length));
  if (topCards.length === 0) {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
        <span className="label">Play pile empty</span>
        <span className="label" style={{ fontSize: 11 }}>
          {state.players[state.currentSeat]?.displayName ?? "?"} leads
        </span>
      </div>
    );
  }
  return (
    <div className="center-pile" style={{ flexDirection: "column", padding: 8, gap: 6 }}>
      <div className="swoop-pile-row">
        {topCards.map((c, i) => (
          <PlayingCard
            key={i}
            card={c}
            width={Math.round(cardWidth * 0.55)}
            style={{
              marginLeft: i === 0 ? 0 : -Math.round(cardWidth * 0.35),
            }}
          />
        ))}
      </div>
      <div className="label" style={{ fontSize: 11 }}>
        TOP: {state.topRank ?? "—"}
        {state.topRunLength > 1 && ` · run of ${state.topRunLength}`}
        {" · "}
        {state.playPile.length} card{state.playPile.length === 1 ? "" : "s"} in pile
      </div>
      {state.swoopedPileCount > 0 && (
        <div className="label muted" style={{ fontSize: 10 }}>
          {state.swoopedPileCount} pile{state.swoopedPileCount === 1 ? "" : "s"} swooped
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action bar
// ---------------------------------------------------------------------------

function SwoopActionBar({
  selectedCount,
  canPlay,
  pileCount,
  handEmpty,
  onPlay,
  onPickup,
}: {
  selectedCount: number;
  canPlay: boolean;
  pileCount: number;
  handEmpty: boolean;
  onPlay: () => void;
  onPickup: () => void;
}) {
  return (
    <div className="action-bar" style={{ gap: 8 }}>
      <button
        className="primary"
        disabled={!canPlay}
        onClick={onPlay}
      >
        {canPlay
          ? `Play ${selectedCount} card${selectedCount === 1 ? "" : "s"}`
          : "Select cards to play"}
      </button>
      <button
        disabled={pileCount === 0}
        onClick={onPickup}
        title={pileCount === 0 ? "Pile is empty" : "Pick up the play pile"}
      >
        Pick up pile ({pileCount})
      </button>
      {handEmpty && (
        <span className="muted" style={{ fontSize: 11 }}>
          Hand empty — click a face-up card, or flip a mystery once its
          face-up is gone.
        </span>
      )}
    </div>
  );
}

